import { CommonModule, DecimalPipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { LicensePricingService } from '../../../shared/services/kyc.service';
import {
  LicensePricing,
} from '../../../shared/types/kyc.types';
import {
  LICENSE_TIER_LABEL,
  LICENSE_TIER_OPTIONS,
  LicenseTier,
} from '../../../shared/types/operator.types';

@Component({
  selector: 'app-license-pricing',
  imports: [
    CommonModule,
    DecimalPipe,
    ReactiveFormsModule,
    PageHeaderComponent,
    DataStateComponent,
    ModalComponent,
  ],
  templateUrl: './license-pricing.component.html',
})
export class LicensePricingComponent implements OnInit {
  private readonly service = inject(LicensePricingService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly items = signal<LicensePricing[]>([]);

  readonly tierLabel = LICENSE_TIER_LABEL;
  readonly allTiers = LICENSE_TIER_OPTIONS.map((o) => o.value);

  /** Tiers that don't have a pricing row yet (offered as "create" buttons). */
  readonly missingTiers = computed<LicenseTier[]>(() => {
    const have = new Set(this.items().map((p) => p.tier));
    return this.allTiers.filter((t) => !have.has(t));
  });

  // ── Edit modal ────────────────────────────────────────────────────────
  readonly editOpen = signal(false);
  readonly editTier = signal<LicenseTier | null>(null);
  readonly editSaving = signal(false);
  readonly editError = signal<string | null>(null);
  readonly editForm = this.fb.group({
    price: [0, [Validators.required, Validators.min(1)]],
    durationUnlimited: [false],
    durationDays: [30, [Validators.required, Validators.min(1)]],
    maxUnlimited: [false],
    maxPrestataires: [10, [Validators.required, Validators.min(1)]],
    description: [''],
  });

  ngOnInit(): void { this.load(); }

  /**
   * Synchronise l'état activé/désactivé des champs numériques selon les cases
   * « illimité » : un contrôle désactivé est exclu de `form.invalid` et sa
   * valeur est ignorée au submit (envoyée `null`). Appelé à l'ouverture du
   * modal et à chaque (dé)cochage.
   */
  syncUnlimited(): void {
    const f = this.editForm.controls;
    f.durationUnlimited.value ? f.durationDays.disable() : f.durationDays.enable();
    f.maxUnlimited.value ? f.maxPrestataires.disable() : f.maxPrestataires.enable();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.service
      .list()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rows) => {
          this.items.set(
            [...(rows ?? [])].sort((a, b) =>
              this.allTiers.indexOf(a.tier) - this.allTiers.indexOf(b.tier),
            ),
          );
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.items.set([]);
          this.error.set(toErrorMessage(err));
        },
      });
  }

  openEdit(tier: LicenseTier): void {
    const existing = this.items().find((p) => p.tier === tier);
    this.editTier.set(tier);
    this.editForm.reset({
      price: existing?.price ?? 0,
      durationUnlimited: existing ? existing.durationDays == null : false,
      durationDays: existing?.durationDays ?? 30,
      maxUnlimited: existing ? existing.maxPrestataires == null : false,
      maxPrestataires: existing?.maxPrestataires ?? 10,
      description: existing?.description ?? '',
    });
    this.syncUnlimited();
    this.editError.set(null);
    this.editOpen.set(true);
  }
  closeEdit(): void {
    if (this.editSaving()) return;
    this.editOpen.set(false);
  }
  submitEdit(): void {
    const tier = this.editTier();
    if (!tier || this.editForm.invalid || this.editSaving()) {
      this.editForm.markAllAsTouched();
      return;
    }
    const raw = this.editForm.getRawValue();
    this.editSaving.set(true);
    this.editError.set(null);
    this.service
      .set(tier, {
        price: raw.price,
        durationDays: raw.durationUnlimited ? null : raw.durationDays,
        maxPrestataires: raw.maxUnlimited ? null : raw.maxPrestataires,
        description: raw.description.trim() || null,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.editSaving.set(false);
          this.editOpen.set(false);
          this.load();
        },
        error: (err: unknown) => {
          this.editSaving.set(false);
          this.editError.set(toErrorMessage(err, 'errors.updateFailed'));
        },
      });
  }
}
